// SelectInput.jsx
import { useField, useFormikContext } from 'formik'
import React, { CSSProperties, useMemo } from 'react'
import ReactSelect, { StylesConfig } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import AsyncSelect from 'react-select/async'
import AsyncCreatableSelect from 'react-select/async-creatable'
import { getAddress, getAddresses, useSelect } from '@velocity/shared'
import { useTranslation } from 'react-i18next'
import { CFieldProps, GroupedOption, OptionType } from '@velocity/shared'
import chroma from 'chroma-js'

type InputProps = CFieldProps & {
  hideIndicator?: boolean
}
export const CSelect = ({
  validator,
  hideIndicator,
  className,
  required,
  ...props
}: InputProps) => {
  const { t } = useTranslation()
  const [, meta, helpers] = useField(props.name)
  const { setFieldValue, values } = useFormikContext<any>()
  const {
    updatePageParams,
    isFetching,
    selected,
    options,
    setLocalSelections
  } = useSelect({
    ...props,
    meta,
    values,
    translator: t
  })

  const onChange = (val: any) => {
    // setCreateVal('')
    setLocalSelections(val)
    if (props.type === 'address') {
      getAddress(props.name, val, setFieldValue, props.addressProps?.fields)
    } else if (props.createFn && val.__isNew__) {
      props.createFn(val)
      // setCreateVal(val.value)
    } else {
      if (props.selectFn) props.selectFn(val, props.name)
      else if (props.isMulti) {
        helpers.setValue(val.map((v: any) => v.value))
      } else {
        helpers.setValue(val.value)
      }
    }
  }
  const groupStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }
  const groupBadgeStyles: CSSProperties = {
    backgroundColor: '#EBECF0',
    borderRadius: '2em',
    color: '#172B4D',
    display: 'inline-block',
    fontSize: 12,
    fontWeight: 'normal',
    lineHeight: '1',
    minWidth: 1,
    padding: '0.16666666666667em 0.5em',
    textAlign: 'center'
  }

  const formatGroupLabel = (data: GroupedOption) => (
    <div style={groupStyles}>
      <span>{data.label}</span>
      {!data.noCount && (
        <span style={groupBadgeStyles}>{data.options.length}</span>
      )}
    </div>
  )
  const onInputChange = (inputValue: string) => {
    props.searchFn?.(inputValue)
    if (props.optionsQuery?.search) updatePageParams('search', inputValue)
  }
  const handleMenuOpen = () => {
    requestAnimationFrame(() => {
      const menu = document.querySelector('.react-select__menu-list')
      const selected = menu?.querySelector('.react-select__option--is-selected')
      selected?.scrollIntoView({ block: 'nearest' })
    })
  }
  const commonProps: any = useMemo(
    () => ({
      ...props,
      components:
        hideIndicator ?
          {
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null
          }
        : {},
      styles: {
        control: (base: any) => ({
          ...base,
          borderColor:
            meta?.touched && meta?.error ? '#ef4444' : base.borderColor,
          '&:hover': {
            borderColor:
              meta?.touched && meta?.error ? '#ef4444' : base.borderColor
          }
        }),
        placeholder: (base: any) => ({
          ...base,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', // truncate long placeholder
          maxWidth: 'calc(100% - 20px)',
          ...(props.name === 'color' ? dot('#ccc') : {})
        }),
        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
        ...(props.name === 'color' ? colourStyles : {})
      },
      isDisabled: props.disabled,
      menuPortalTarget: document.body,
      className: 'basic-multi-select',
      classNamePrefix: 'react-select',
      onChange,
      isLoading: isFetching || props.isLoading,
      onInputChange,
      options,
      loadOptions: props.type === 'address' ? getAddresses : props.loadOptions,
      value: selected,
      menuShouldScrollIntoView: true,
      cacheOptions: true,
      closeMenuOnSelect: !props.isMulti,
      onMenuOpen: handleMenuOpen
      // menuPlacement:"auto"
    }),
    // eslint-disable-next-line
    [selected, options, isFetching, props.isLoading]
  )
  if (props.grouped) commonProps['formatGroupLabel'] = formatGroupLabel
  return (
    <div
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {(props.createFn || props.createAble) && props.loadOptions ?
        <AsyncCreatableSelect {...commonProps} defaultOptions />
      : props.createFn || props.createAble ?
        <CreatableSelect
          {...commonProps}
          isValidNewOption={(inputValue: any) =>
            validator ? validator(inputValue) : true
          }
        />
      : props.loadOptions || props.type === 'address' ?
        <AsyncSelect {...commonProps} defaultOptions />
      : <ReactSelect {...commonProps} />}
    </div>
  )
}

const dot = (color = 'transparent') => ({
  alignItems: 'center',
  display: 'flex',

  ':before': {
    backgroundColor: color,
    borderRadius: 10,
    content: '" "',
    display: 'block',
    marginRight: 8,
    height: 10,
    width: 10
  }
})
const colourStyles: StylesConfig<OptionType> = {
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    const color = chroma(data.value)
    return {
      ...styles,
      backgroundColor:
        isDisabled ? undefined
        : isSelected ? data.value
        : isFocused ? color.alpha(0.1).css()
        : undefined,
      color:
        isDisabled ? '#ccc'
        : isSelected ?
          chroma.contrast(color, 'white') > 2 ?
            'white'
          : 'black'
        : data.value,
      cursor: isDisabled ? 'not-allowed' : 'default',

      ':active': {
        ...styles[':active'],
        backgroundColor:
          !isDisabled ?
            isSelected ? data.value
            : color.alpha(0.3).css()
          : undefined
      }
    }
  },
  input: (styles) => ({ ...styles, ...dot() }),
  singleValue: (styles, { data }) => ({ ...styles, ...dot(data.value) })
}
